// src/services/userService.js
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

export const userService = {
  // Get user profile with retry
  async getUserProfile(userId, maxRetries = 2) {
    let retries = 0;
    let lastError = null;
    
    while (retries <= maxRetries) {
      try {
        // Refresh token before trying
        if (retries > 0) {
          await auth.currentUser?.getIdToken(true);
        }
        
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          return userSnap.data();
        } else {
          return null;
        }
      } catch (error) {
        console.error(`Error fetching user (attempt ${retries + 1}):`, error);
        lastError = error;
        retries++;
        
        // Small delay before retry
        if (retries <= maxRetries) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    }
    
    // If we got here, all retries failed
    throw lastError;
  },
  
  // Update user profile with retry
  async updateUserProfile(userId, profileData, maxRetries = 2) {
    let retries = 0;
    let lastError = null;
    
    while (retries <= maxRetries) {
      try {
        // Force token refresh on each retry
        if (retries > 0) {
          await auth.currentUser?.getIdToken(true);
        }
        
        const userRef = doc(db, "users", userId);
        
        // Get existing data first
        let existingData = {};
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          existingData = userSnap.data();
        }
        
        // Merge with new data
        const mergedData = {
          ...existingData,
          ...profileData,
          updatedAt: Timestamp.now()
        };
        
        // Write to Firestore
        await setDoc(userRef, mergedData);
        return mergedData;
      } catch (error) {
        console.error(`Error updating user (attempt ${retries + 1}):`, error);
        lastError = error;
        retries++;
        
        // Small delay before retry
        if (retries <= maxRetries) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    }
    
    // If we got here, all retries failed
    throw lastError;
  },
  
  // Create complete profile
  async completeProfile(userId, profileData) {
    try {
      // Force token refresh
      await auth.currentUser?.getIdToken(true);
      
      const userRef = doc(db, "users", userId);
      
      // Check if user exists
      const userSnap = await getDoc(userRef);
      
      const completeData = {
        ...profileData,
        licenseAgreedDate: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      if (userSnap.exists()) {
        // Update existing profile
        const existingData = userSnap.data();
        await setDoc(userRef, {
          ...existingData,
          ...completeData
        });
      } else {
        // Create new profile
        await setDoc(userRef, {
          ...completeData,
          registrationDate: Timestamp.now(),
          yearlyBoardCount: 0,
          lastResetDate: Timestamp.now(),
          authProvider: auth.currentUser?.providerData[0]?.providerId || 'email'
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error completing profile:", error);
      throw error;
    }
  }
};